import { ApplicationServerService, DatabaseApplicationService, PortfolioService } from '@amzn/awscat-portfolio-assessment-service-client';
import { Database, DeserializedServer as Server, InterpretedDatabase, InterpretedServer } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { useCollection } from '@amzn/awsui-collection-hooks';
import {
    Box,
    CollectionPreferences,
    CollectionPreferencesProps,
    Grid,
    Header,
    NonCancelableCustomEvent,
    Pagination,
    Select,
    SelectProps,
    SpaceBetween,
    Spinner,
    Table,
    TableProps } from '@amzn/awsui-components-react';
import React, { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { MatchParam } from '../../common/types';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { InfoLink } from '../common/InfoLink';
import PreviewBanner from '../common/PreviewBanner';
import ApplicationResourceDatabaseDetails from './ApplicationResourceDatabaseDetails';
import ApplicationResourceServerDetails from './ApplicationResourceServerDetails';
import { buildColumnDefinition } from './BuildColumnDefinition';
import { DatabaseColumnDefinition } from './DatabaseColumnDefinition';
import { ServerColumnDefinition } from './ServerColumnDefinition';

type Props = {
    onInfoLinkClick: (id: string) => void; // TODO: onInfoLinkClick is not implemented yet
} & FlashContextInterface;

const appServerService = new ApplicationServerService();
const dbToAppService = new DatabaseApplicationService();
const portfolioService = new PortfolioService();
function ApplicationResourcePage({ onInfoLinkClick, flash }: Props) {
    const isMounted = useIsMounted();

    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/management/application-detail/:applicationId',
    });
    const portfolioId = match?.params?.portfolioId || '';
    const applicationId = match?.params?.applicationId || '';
    const [loading, setLoading] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);
    const [servers, setServers] = useState<Array<InterpretedServer<Server>>>([]);
    const [serversToDisplay, setServersToDisplay] = useState<Array<InterpretedServer<Server>>>([]);
    const [missingServers, setMissingServers] = useState<string[]>([]);
    const [databases, setDatabases] = useState<Array<InterpretedDatabase<Database>>>([]);
    const [databasesToDisplay, setDatabasesToDisplay] = useState<Array<InterpretedDatabase<Database>>>([]);
    const [missingDatabases, setMissingDatabases] = useState<string[]>([]);
    const { panelHandler } = useContext(HelpPanelContext);

    const environmentOptions: SelectProps.Option[] = [
        {label: constants.ALL_ENVIRONMENTS, value: 'all'},
        {label: constants.PRODUCTION, value: 'production'},
        {label: constants.NON_PRODUCTION, value: 'non-prod'},
    ];
    const [activeEnvironment, setActiveEnvironment] = useState<SelectProps.Option>(environmentOptions[0]);
    const [selectedServers, setSelectedServers] = useState<InterpretedServer<Server>[]>([]);
    const [selectedDatabases, setSelectedDatabases] = useState<InterpretedDatabase<Database>[]>([]);
    const [tcoAssessmentId, setTcoAssessmentId] = useState<string | undefined>('');

    async function loadData() {
        setLoading(true);
        try {
            const portfolio = await portfolioService.get(portfolioId);
            if (isMounted()) {
                setTcoAssessmentId(portfolio.defaultTcoAssessmentId);
            }
            const appServersMapping = await appServerService.getServersForAppIdWithInterpretation(
                applicationId, portfolioId, portfolio.defaultTcoAssessmentId || '');
            const { mappedServers, unmappedServers } = appServersMapping.reduce((result, mapping) => {
                if (mapping.server) {
                    result.mappedServers.push(mapping.server);
                } else {
                    result.unmappedServers.push(mapping.serverId);
                }
                return result;
            }, {mappedServers: [] as InterpretedServer<Server>[], unmappedServers: [] as string[]});
            if (isMounted()) {
                setServers(mappedServers);
                setServersToDisplay(mappedServers);
                setMissingServers(unmappedServers);
            }

            const databaseAppMapping = await dbToAppService.getDatabasesForAppId(applicationId, portfolioId, portfolio.defaultTcoAssessmentId || '');
            const { mappedDatabases, unmappedDatabases } = databaseAppMapping.reduce((result, mapping) => {
                if (mapping.database) {
                    result.mappedDatabases.push(mapping.database);
                } else {
                    result.unmappedDatabases.push(mapping.databaseId);
                }
                return result;
            }, {mappedDatabases: [] as InterpretedDatabase<Database>[], unmappedDatabases: [] as string[]});
            if (isMounted()) {
                setDatabases(mappedDatabases);
                setDatabasesToDisplay(mappedDatabases);
                setMissingDatabases(unmappedDatabases);
            }
        } catch (e) {
            if (isMounted()) {
                flash.alert(FlashType.error, constants.ENCOUNTERED_ERROR_LOADING_APP, (e as Error).message);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    }

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.APPLICATION_RESOURCES);
    }, [ panelHandler ]);

    useEffect(() => {
        loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ onInfoLinkClick ]);

    async function filterByEnvironment() {
        if (!isMounted()) {
            return;
        }
        setTableLoading(true);
        try {
            if (activeEnvironment.value !== 'all') {
                const isProd = activeEnvironment.value === 'production';
                const regExComparison = new RegExp('^(prd|prod)', 'i');
                const filteredServers = servers.filter(server => {
                    return isProd ? regExComparison.test(server.serverEnvironment) : !regExComparison.test(server.serverEnvironment);
                });
                const filteredDatabases = databases.filter(database => {
                    return isProd ? regExComparison.test(database.interpretedFields?.environment.label || '')
                        : !regExComparison.test(database.interpretedFields?.environment.label || '');
                });
                setServersToDisplay(filteredServers);
                setDatabasesToDisplay(filteredDatabases);
            } else {
                setServersToDisplay(servers);
                setDatabasesToDisplay(databases);
            }
        } catch (e) {
            if (isMounted()) {
                flash.alert(FlashType.error, constants.ENCOUNTERED_ERROR_FILTERING_BASED_ON_ENVIRONMENT, (e as Error).message);
            }
        } finally {
            if (isMounted()) {
                setTableLoading(false);
            }
        }
    }

    useEffect(() => {
        filterByEnvironment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeEnvironment]);

    const environmentChange = async (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        const { selectedOption } = event.detail;
        setActiveEnvironment(selectedOption);
    };

    const gridDefinition = [
        {colspan: {l: 6, m: 6, default: 12} },
        {colspan: {default: 12} },
        {colspan: {default: 12} },
        {colspan: {default: 12} },
        {colspan: {default: 12} },
        {colspan: {default: 12} },
    ];

    const header = (
        <Header
            variant='h1'
            className='awsui-util-mb-l'
        >
            <SpaceBetween direction='horizontal' size='xs'>
                <Box variant='h1' display='inline'>{constants.RESOURCES}</Box>
                <InfoLink className='data-quality-info-link' topic={HelpPanelTopicOptions.APPLICATION_RESOURCES} />
            </SpaceBetween>
        </Header>
    );

    const environmentSelect = (
        <div className='awsui-util-d-ib'>
            <h3 className='awsui-util-d-ib awsui-util-pr-l'>{constants.ENVIRONMENTS}: </h3>
            <Select
                className='awsui-util-d-ib'
                options={environmentOptions}
                selectedOption={activeEnvironment}
                onChange={environmentChange}
            />
        </div>
    );

    const serverAttributesToExclude: Array<keyof Server> = [
        'portfolioId',
        'migrationPlanOverride',
        'ec2RecommendationOverrideOnPremCapacity',
        'ec2RecommendationOverridePeakUtilization',
        'ec2RecommendationOverrideAvgUtilization',
        'serverSupportId',
        'database',
        'databaseID',
        'databaseName',
        'databaseSizeGB',
        'databaseVersion',
        'databaseEngine',
        'isSharedInfrastructure',
        'inScope',
        'createdAt',
        'updatedAt',
    ];

    const serverColumnDefinition = buildColumnDefinition(ServerColumnDefinition, servers, serverAttributesToExclude);

    const emptyTable = (
        <Box textAlign='center' color='inherit'>
            <b>{constants.NO_RESOURCES}</b>
            <Box
                padding={{ bottom: 's' }}
                variant='p'
                color='inherit'
            >
                {constants.NO_RESOURCES_TO_DISPLAY}
            </Box>
        </Box>
    );

    const serverTableHeader = (
        <Header>
            {constants.SERVERS}
        </Header>
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const serverDetail = !loading && selectedServers.length === 1 && (
        <ApplicationResourceServerDetails
            server={selectedServers[0]}
            tcoAssessmentId={tcoAssessmentId}
            portfolioId={portfolioId}
            onInfoLinkClick={onInfoLinkClick}
        />
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const databaseDetail = !loading && selectedDatabases.length === 1 && (
        <ApplicationResourceDatabaseDetails
            database={selectedDatabases[0]}
            tcoAssessmentId={tcoAssessmentId}
            portfolioId={portfolioId}
            onInfoLinkClick={onInfoLinkClick}
        />
    );

    function serverSelectionChanged(event: NonCancelableCustomEvent<TableProps.SelectionChangeDetail<InterpretedServer<Server>>>): void {
        setSelectedServers(event.detail.selectedItems);
    }

    function databaseSelectionChanged(event: NonCancelableCustomEvent<TableProps.SelectionChangeDetail<Database>>): void {
        setSelectedDatabases(event.detail.selectedItems);
    }

    useEffect(() => {
        if (serverDetail) {
            const details = document.getElementById('serverDetails');
            details?.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }, [serverDetail]);

    useEffect(() => {
        if (databaseDetail) {
            const details = document.getElementById('databaseDetails');
            details?.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }, [databaseDetail]);

    const serverPageSizePreferences = ({
        options: [
            {value: 5, label: constants.FIVE_SERVERS},
            {value: 10, label: constants.TEN_SERVERS},
            {value: 15, label: constants.FIFTEEN_SERVERS},
        ],
        title: constants.PAGE_SIZE,
    });

    const databasePageSizePreferences = ({
        options: [
            {value: 5, label: constants.FIVE_DBS},
            {value: 10, label: constants.TEN_DBS},
            {value: 15, label: constants.FIFTEEN_DBS},
        ],
        title: constants.PAGE_SIZE,
    });

    function serverPreferencesChanged(event: NonCancelableCustomEvent<CollectionPreferencesProps.Preferences<any>>): void {
        setServerPreferences({pageSize: event.detail.pageSize || 10});
    }

    function databasePreferencesChanged(event: NonCancelableCustomEvent<CollectionPreferencesProps.Preferences<any>>): void {
        setDatabasePreferences({pageSize: event.detail.pageSize || 10});
    }

    const [serverPreferences, setServerPreferences] = React.useState({
        pageSize: 5,
    });

    const [databasePreferences, setDatabasePreferences] = React.useState({
        pageSize: 5,
    });

    const serverTablePreferences = (
        <CollectionPreferences
            onConfirm={serverPreferencesChanged}
            title={constants.PREFERENCE_HEADER}
            confirmLabel={constants.CONFIRM_BUTTON_TEXT}
            cancelLabel={constants.CANCEL_BUTTON_TEXT}
            preferences={serverPreferences}
            pageSizePreference={serverPageSizePreferences}
        />
    );

    const databaseTablePreferences = (
        <CollectionPreferences
            onConfirm={databasePreferencesChanged}
            title={constants.PREFERENCE_HEADER}
            confirmLabel={constants.CONFIRM_BUTTON_TEXT}
            cancelLabel={constants.CANCEL_BUTTON_TEXT}
            preferences={databasePreferences}
            pageSizePreference={databasePageSizePreferences}
        />
    );

    const serverCollectionResult = useCollection(serversToDisplay, {pagination: {pageSize: serverPreferences.pageSize}});
    const serverPagination = (
        <Pagination
            {...serverCollectionResult.paginationProps}
        />
    );

    const databaseCollectionResult = useCollection(databasesToDisplay, {pagination: {pageSize: databasePreferences.pageSize}});
    const databasePagination = (
        <Pagination
            {...databaseCollectionResult.paginationProps}
        />
    );

    const serverTable = (
        <Table
            columnDefinitions={serverColumnDefinition}
            items={serverCollectionResult.items}
            loadingText={constants.LOADING_SERVERS}
            loading={tableLoading}
            empty={emptyTable}
            resizableColumns
            header={serverTableHeader}
            pagination={serverPagination}
            preferences={serverTablePreferences}
            selectionType='multi'
            selectedItems={selectedServers}
            onSelectionChange={serverSelectionChanged}
        />
    );

    const missingServerContent = missingServers.length ? (
        <p>{constants.MISSING_SERVERS} {missingServers.join(', ')}</p>
    ) : (null);

    const databaseAttributesToExclude: Array<keyof InterpretedDatabase<Database>> = [
        'portfolioId',
        'dbOwnerId',
        'isSharedInfrastructure',
        'inScope',
        'createdAt',
        'updatedAt',
    ];

    const databaseColumnDefinition = buildColumnDefinition(DatabaseColumnDefinition, databases, databaseAttributesToExclude);

    const databaseTableHeader = (
        <Header>
            {constants.DATABASES}
        </Header>
    );

    const databaseTable = (
        <Table
            columnDefinitions={databaseColumnDefinition}
            items={databaseCollectionResult.items}
            loadingText={constants.LOADING_DATABASES}
            loading={tableLoading}
            empty={emptyTable}
            resizableColumns
            header={databaseTableHeader}
            pagination={databasePagination}
            preferences={databaseTablePreferences}
            selectionType='multi'
            selectedItems={selectedDatabases}
            onSelectionChange={databaseSelectionChanged}
        />
    );

    const missingDatabaseContent = missingDatabases.length ? (
        <p>{constants.MISSING_DATABASES}{missingDatabases.join(', ')}</p>
    ) : (null);

    const content = !loading && (
        <Grid
            gridDefinition={gridDefinition}
        >
            {environmentSelect}
            {serverTable}
            {missingServerContent}
            {databaseTable}
            {missingDatabaseContent}
            {serverDetail}
            {databaseDetail}
        </Grid>
    );

    return (
        <div className='awsui' data-testid='applicationResourcePage'>
            <PreviewBanner />
            {header}
            {loading ? <Spinner size='big' /> : null}
            {content}
        </div>
    );
}

export default withFlashContext(ApplicationResourcePage);
